import React from 'react';
import {
  Card,
  makeStyles,
  mergeClasses,
  Skeleton as FluentSkeleton,
  SkeletonItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  // New skeleton variants
  statValue: { width: '120px', height: '36px', marginBottom: '0.5rem' },
  statLabel: { width: '80px', height: '14px' },
  pageLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '0.75rem',
    paddingTop: '4rem',
    paddingBottom: '4rem',
  },
  // Existing table skeleton styles
  cell: { width: '70%', height: '16px' },
  cellPrimary: { width: '90%' },
  headerCell: { width: '60%', height: '12px' },
});

// ---------------------------------------------------------------------------
// Base skeleton — a single shimmering rectangle
// ---------------------------------------------------------------------------

interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

export function Skeleton({ className = '', style }: SkeletonProps) {
  return (
    <FluentSkeleton aria-label="Loading content">
      <SkeletonItem className={className} style={style} />
    </FluentSkeleton>
  );
}

// ---------------------------------------------------------------------------
// Stat card skeleton — matches stat/metric card layout
// ---------------------------------------------------------------------------

export function StatCardSkeleton() {
  const styles = useStyles();
  return (
    <Card>
      <FluentSkeleton aria-label="Loading statistic">
        <SkeletonItem className={styles.statValue} />
        <SkeletonItem className={styles.statLabel} />
      </FluentSkeleton>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Stats grid skeleton — renders N StatCardSkeleton components
// ---------------------------------------------------------------------------

interface StatsGridSkeletonProps {
  count?: number;
}

export function StatsGridSkeleton({ count = 4 }: StatsGridSkeletonProps) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page loading — centered full-page spinner
// ---------------------------------------------------------------------------

export function PageLoading() {
  const styles = useStyles();
  return (
    <div className={styles.pageLoading}>
      <Spinner size="huge" label="Loading…" labelPosition="below" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table skeleton helpers (existing — do not modify signatures)
// ---------------------------------------------------------------------------

interface TableRowSkeletonProps {
  columns: number;
}

export function TableRowSkeleton({ columns }: TableRowSkeletonProps) {
  const styles = useStyles();
  return (
    <TableRow>
      {Array.from({ length: columns }).map((_, i) => (
        <TableCell key={i}>
          <FluentSkeleton aria-label="Loading row">
            <SkeletonItem
              className={mergeClasses(styles.cell, i === 0 ? styles.cellPrimary : undefined)}
            />
          </FluentSkeleton>
        </TableCell>
      ))}
    </TableRow>
  );
}

interface TableSkeletonProps {
  columns: number;
  rows?: number;
  headers?: string[];
}

export function TableSkeleton({ columns, rows = 5, headers }: TableSkeletonProps) {
  const styles = useStyles();
  return (
    <Table aria-label="Loading">
      <TableHeader>
        <TableRow>
          {headers
            ? headers.map((header, i) => <TableHeaderCell key={i}>{header}</TableHeaderCell>)
            : Array.from({ length: columns }).map((_, i) => (
                <TableHeaderCell key={i}>
                  <FluentSkeleton aria-label="Loading header">
                    <SkeletonItem className={styles.headerCell} />
                  </FluentSkeleton>
                </TableHeaderCell>
              ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: rows }).map((_, i) => (
          <TableRowSkeleton key={i} columns={columns} />
        ))}
      </TableBody>
    </Table>
  );
}
